import { useState } from "react";

import { cn } from "@/shared/lib/cn";

const TONES = [
  "bg-tone-1 text-tone-1-ink",
  "bg-tone-2 text-tone-2-ink",
  "bg-tone-3 text-tone-3-ink",
  "bg-tone-4 text-tone-4-ink",
  "bg-tone-5 text-tone-5-ink",
  "bg-tone-6 text-tone-6-ink",
] as const;

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = Array.from(words[0] ?? "?")[0] ?? "?";
  const last = words.length > 1 ? (Array.from(words.at(-1) ?? "")[0] ?? "") : "";
  return /\p{Script=Arabic}/u.test(first) ? first : (first + last).toUpperCase();
}

function toneOf(name: string): string {
  let hash = 0;
  for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return TONES[hash % TONES.length] ?? TONES[0];
}

interface AvatarProps {
  name: string;
  /** The person's picture; without one, or when it fails to load, their initials show. */
  src?: string | null | undefined;
  className?: string;
}

/**
 * A person's picture, or their initials on their own stable pastel, with a hairline edge so it
 * holds on any fill.
 */
export function Avatar({ name, src, className }: AvatarProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const showPicture = Boolean(src) && failed !== src;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative grid size-8 shrink-0 place-items-center overflow-hidden rounded-full text-[13px] font-semibold tracking-[0.01em] ring-1 ring-ink/[0.06] select-none ring-inset",
        toneOf(name),
        className,
      )}
    >
      {showPicture ? (
        <img
          src={src ?? undefined}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 size-full object-cover"
          onError={() => {
            setFailed(src ?? null);
          }}
        />
      ) : (
        initials(name)
      )}
    </span>
  );
}
